import { Button, Column, Modal, Row, Typography, useLoading } from "best-common-react";
import React, { useEffect, useState } from "react";
import { saveEventOverrides } from "../../api/RequesTixApi";
import { defaultAccess, defaultCutoff } from "../../constants/CutoffConstants";
import { DepartmentByGameDTO } from "../../types/Department";
import { convertToEventTypeDTO } from "../../util/CutoffUtil";
import PersonalOnOff from "../requests/personal/PersonalOnOff";
import EventAccessAndCutoff from "./EventAccessAndCutoff";

type EventAccessBulkOverrideProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void | Promise<void>;
  departments: DepartmentByGameDTO[];
  selectedDepartmentIds?: number[];
  gamePk: number | string;
  timezone?: string;
  personalRequestsOverride: boolean | null;
  setPersonalRequestsOverride: (value: boolean | null) => void;
  accessOverride: any;
  setAccessOverride: (value: any) => void;
  cutoffOverride: any;
  setCutoffOverride: (value: any) => void;
};

const EventAccessBulkOverride = ({
  isOpen,
  onClose,
  onSave,
  departments,
  selectedDepartmentIds = [],
  gamePk,
  timezone,
  personalRequestsOverride,
  setPersonalRequestsOverride,
  accessOverride,
  setAccessOverride,
  cutoffOverride,
  setCutoffOverride,
}: EventAccessBulkOverrideProps) => {
  const { setLoading } = useLoading();
  const [canSave, setCanSave] = useState<boolean>(false);

  const selectedDepartments: DepartmentByGameDTO[] = departments.filter((dept: DepartmentByGameDTO) =>
    selectedDepartmentIds.includes(dept.departmentId),
  );

  const save = async () => {
    try {
      const access = accessOverride.active ? accessOverride : null;
      const cutoff = cutoffOverride.active ? cutoffOverride : null;

      setLoading(true);
      await saveEventOverrides({
        departmentIds: selectedDepartmentIds,
        gamePk: Number(gamePk),
        personalRequestsOverride,
        accessOverride: access,
        //@ts-ignore
        cutoffOverride: convertToEventTypeDTO(cutoff),
      });

      if (onSave) {
        onSave();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setAccessOverride(defaultAccess);
      setCutoffOverride(defaultCutoff);
      setPersonalRequestsOverride(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const ableToSave =
      selectedDepartmentIds.length > 0 &&
      (accessOverride.active || cutoffOverride.active || personalRequestsOverride != null);
    setCanSave(ableToSave);
  }, [accessOverride, cutoffOverride, personalRequestsOverride, selectedDepartmentIds]);

  return (
    <>
      {!!isOpen && (
        <Modal show={isOpen} size="xl">
          <Modal.Header close={onClose}>Bulk Override Ticket Access</Modal.Header>
          <Modal.Body>
            <div className="bcr-mb-3">
              <Typography elementStyles={{ weight: "bold" }}>
                {selectedDepartments.length} Department(s) Selected
              </Typography>
              <Typography>{selectedDepartments.map((department) => department.departmentName).join(", ")}</Typography>
            </div>
            <Row className="bcr-my-2">
              <Column width={2}>
                <PersonalOnOff
                  value={personalRequestsOverride}
                  onChange={(value) => setPersonalRequestsOverride(value)}
                />
              </Column>
            </Row>
            <EventAccessAndCutoff
              accessOverride={accessOverride}
              setAccessOverride={setAccessOverride}
              cutoffOverride={cutoffOverride}
              setCutoffOverride={setCutoffOverride}
              timezone={timezone}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={save} disabled={!canSave}>
              Save Overrides
            </Button>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default EventAccessBulkOverride;
